import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { Plus, Landmark, X, Trash2, ChevronDown, TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";

function fmt(n, ccy = "GBP") {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy, maximumFractionDigits: 0 }).format(n || 0);
  } catch {
    return `${ccy} ${(Number(n) || 0).toFixed(0)}`;
  }
}

function paceColor(summary) {
  if (summary?.percent_used == null) return "#00F5D4";
  if (summary.on_pace_to_overshoot) return "#f43f5e";
  if (summary.percent_used >= 85) return "#f59e0b";
  return "#00F5D4";
}

const EMPTY_FORM = {
  name: "", period_start: new Date().toISOString().slice(0, 10),
  period_end: new Date(new Date().getFullYear(), 11, 31).toISOString().slice(0, 10),
  total_budget: "", currency: "GBP", notes: "",
};

export default function CompanyBudgets() {
  const [budgets, setBudgets] = useState([]);
  const [detail, setDetail] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/company-budgets");
      setBudgets(data || []);
    } catch {
      toast.error("Failed to load company budgets");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (b) => {
    try {
      const { data } = await api.get(`/company-budgets/${b.company_budget_id}`);
      setDetail(data);
    } catch {
      toast.error("Failed to load budget");
    }
  };

  const create = async () => {
    if (!form.name.trim() || !form.total_budget) return toast.error("Name and total budget are required");
    setSaving(true);
    try {
      await api.post("/company-budgets", {
        ...form,
        total_budget: parseFloat(form.total_budget) || 0,
      });
      toast.success("Company budget created");
      setShowModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create budget");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete budget "${detail.name}"?`)) return;
    try {
      await api.delete(`/company-budgets/${detail.company_budget_id}`);
      toast.success("Budget deleted");
      setDetail(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete budget");
    }
  };

  if (detail) {
    const s = detail.summary;
    return (
      <DarkPage><div className="p-6 max-w-4xl mx-auto">
        <button onClick={() => setDetail(null)} className="text-xs text-[#7d8590] hover:text-white mb-4 flex items-center gap-1">
          <ChevronDown className="w-3 h-3 rotate-90" /> Back
        </button>
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">{detail.name}</h1>
            <p className="text-sm text-[#7d8590] mt-1">{detail.period_start} → {detail.period_end}</p>
          </div>
          <button onClick={remove} className="text-xs px-3 py-1.5 rounded-full border border-rose-500/20 text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 flex items-center gap-1">
            <Trash2 className="w-3 h-3" /> Delete
          </button>
        </div>

        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs text-[#7d8590]">{Math.round((s.elapsed_fraction || 0) * 100)}% of period elapsed</div>
            {s.on_pace_to_overshoot ? (
              <span className="text-xs px-2 py-0.5 rounded-full border border-rose-500/20 text-rose-400 bg-rose-500/10 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> On pace to overshoot
              </span>
            ) : (
              <span className="text-xs px-2 py-0.5 rounded-full border border-[#00F5D4]/20 text-[#00F5D4] bg-[#00F5D4]/10 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> On pace
              </span>
            )}
          </div>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-2xl font-bold text-white">{fmt(s.actual_cost, s.currency)}</span>
            <span className="text-sm text-[#7d8590]">of {fmt(s.total_budget, s.currency)} budgeted</span>
          </div>
          <div className="w-full h-2 bg-[#1c2128] rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, s.percent_used || 0)}%`, background: paceColor(s) }} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
            <div><div className="text-[#7d8590] text-xs mb-0.5">Planned cost</div><div className="text-white font-medium">{fmt(s.planned_cost, s.currency)}</div></div>
            <div><div className="text-[#7d8590] text-xs mb-0.5">Variance to date</div><div className={s.variance < 0 ? "text-rose-400 font-medium" : "text-white font-medium"}>{fmt(s.variance, s.currency)}</div></div>
            <div><div className="text-[#7d8590] text-xs mb-0.5">Forecast at period end</div><div className="text-white font-medium">{s.forecast_total_at_period_end != null ? fmt(s.forecast_total_at_period_end, s.currency) : "—"}</div></div>
            <div>
              <div className="text-[#7d8590] text-xs mb-0.5">Forecast variance</div>
              <div className={`font-medium flex items-center gap-1 ${s.forecast_variance != null && s.forecast_variance < 0 ? "text-rose-400" : "text-white"}`}>
                {s.forecast_variance != null && (s.forecast_variance < 0 ? <TrendingDown className="w-3.5 h-3.5" /> : <TrendingUp className="w-3.5 h-3.5" />)}
                {s.forecast_variance != null ? fmt(s.forecast_variance, s.currency) : "—"}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-[#21262d] text-xs text-[#7d8590] font-medium">By category</div>
            {s.by_category.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-[#7d8590]">No spend recorded yet.</div>
            ) : (
              <div className="divide-y divide-[#21262d]">
                {s.by_category.map(c => (
                  <div key={c.category} className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <span className="text-white capitalize">{c.category.replace("_", " ")}</span>
                    <span className="text-[#7d8590]">{fmt(c.actual_amount, s.currency)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-[#21262d] text-xs text-[#7d8590] font-medium">By project</div>
            {s.by_project.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-[#7d8590]">No project spend recorded yet.</div>
            ) : (
              <div className="divide-y divide-[#21262d]">
                {s.by_project.map(p => (
                  <div key={p.project_id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <span className="text-white truncate">{p.project_name}</span>
                    <span className="text-[#7d8590]">{fmt(p.actual_amount, s.currency)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {detail.notes && (
          <div className="bg-[#0d1117] border border-[#21262d] rounded-lg p-4 mt-6">
            <div className="text-xs text-[#7d8590] mb-1">Notes</div>
            <p className="text-sm text-white whitespace-pre-wrap">{detail.notes}</p>
          </div>
        )}
      </div></DarkPage>
    );
  }

  return (
    <DarkPage><div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Company Budgets</h1>
          <p className="text-sm text-[#7d8590] mt-1">Org-wide spend envelopes for a period, rolled up live from every project's budget lines and vendor bills.</p>
        </div>
        <Button onClick={() => { setForm(EMPTY_FORM); setShowModal(true); }} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
          <Plus className="w-4 h-4 mr-1" /> New Budget
        </Button>
      </div>

      {budgets.length === 0 ? (
        <div className="text-center py-20 text-[#7d8590] text-sm">No company budgets yet. Create one to track org-wide spend for a quarter, year, or department.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {budgets.map(b => (
            <div key={b.company_budget_id} onClick={() => openDetail(b)}
              className="bg-[#0d1117] border border-[#21262d] rounded-lg p-5 cursor-pointer hover:border-[#00F5D4]/30 transition-colors">
              <div className="flex items-center gap-1.5 text-white font-semibold mb-1"><Landmark className="w-3.5 h-3.5 text-[#7d8590]" /> {b.name}</div>
              <div className="text-xs text-[#7d8590] mb-3">{b.period_start} → {b.period_end}</div>
              <div className="text-lg font-bold text-white">{fmt(b.total_budget, b.currency)} <span className="text-xs font-normal text-[#7d8590]">budgeted</span></div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 overflow-y-auto py-8">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">New Company Budget</h2>
              <button onClick={() => setShowModal(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. FY2026 Opex"
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Period start</label>
                  <input type="date" value={form.period_start} onChange={e => setForm(f => ({ ...f, period_start: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Period end</label>
                  <input type="date" value={form.period_end} onChange={e => setForm(f => ({ ...f, period_end: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Total budget *</label>
                  <input type="number" step="0.01" value={form.total_budget} onChange={e => setForm(f => ({ ...f, total_budget: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Currency</label>
                  <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                    {["GBP", "USD", "EUR"].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Notes</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm resize-none" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={create} disabled={saving} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {saving ? "Creating…" : "Create budget"}
              </Button>
              <Button onClick={() => setShowModal(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div></DarkPage>
  );
}
